// src/utils/aguaExcelParser.js
import * as XLSX from 'xlsx';

/**
 * Parsea un archivo Excel (o ArrayBuffer) para la lectura de consumos de agua potable (CEA).
 * Retorna un objeto estructurado con información del recibo y desglose por casas.
 */
export const parseAguaExcel = (fileBuffer) => {
  return new Promise((resolve, reject) => {
    try {
      const workbook = XLSX.read(fileBuffer, { type: 'array' });
      const firstSheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[firstSheetName];
      const rawRows = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

      let totalReciboCEA = 0;
      let totalM3Fracc = 0;
      let precioM3 = 0;
      let periodoLectura = '';
      let mesLectura = '';
      let anoLectura = new Date().getFullYear();
      const casas = [];
      const erroresFormato = [];

      // Buscar metadatos en las filas inferiores o superiores
      rawRows.forEach((row, idx) => {
        if (!row || row.length === 0) return;
        const rowStr = row.map((cell) => String(cell || '')).join(' ');

        // 1. Capturar Total del recibo / Proveedor / CEA
        // Soporta: "Total del recibo: $19,709.38", "Importe servicio de agua recibo: $20,621.86", "Total Factura", "Adeudo Total"
        if (/total\s+del\s+recibo|importe\s+(?:servicio\s+de\s+agua\s+)?recibo|total\s+recibo|factura\s+total|adeudo\s+total|total\s+cea|importe\s+cea/i.test(rowStr)) {
          const match = rowStr.match(/(?:recibo|total|factura|adeudo|cea)[^:\d]*:?\s*\$?\s*([\d,]+(?:\.\d+)?)/i);
          if (match) {
            totalReciboCEA = parseFloat(match[1].replace(/,/g, ''));
          }
        }

        // 2. Capturar Metros cúbicos totales del fraccionamiento
        // Soporta: "Metros cúbicos: 575", "Metros cúbicos firelo: 630.22", "Metros cúbicos condominio: ..."
        if (/metros\s+c[uú]bicos/i.test(rowStr) && !/determinaci[oó]n|metodolog[íi]a|consumindos|dividido/i.test(rowStr)) {
          const match = rowStr.match(/metros\s+c[uú]bicos(?:\s+[\wáéíóú]+)*:?\s*([\d,]+(?:\.\d+)?)/i);
          if (match) {
            const val = parseFloat(match[1].replace(/,/g, ''));
            if (val > 10) {
              totalM3Fracc = val;
            }
          }
        }

        // 3. Capturar Precio por M3
        // Soporta fila superior: ["PRECIO METRO CÚBICO:", "", "", 31.66]
        // y notas inferiores: "Precio por M3 de acuerdo a consumo del condominio: $ 31.66", "Precio por M3 de acuerdo a recibo: $ 34.28"
        if (/precio\s+(?:por\s+)?(?:m3|metro\s+c[uú]bico)/i.test(rowStr) && !/determinaci[oó]n|metodolog[íi]a/i.test(rowStr)) {
          // Intentar coincidencia en texto de notas
          const match = rowStr.match(/precio\s+(?:por\s+)?(?:m3|metro\s+c[uú]bico)[^:\d]*:?\s*\$?\s*([\d,]+(?:\.\d+)?)/i);
          if (match) {
            precioM3 = parseFloat(match[1].replace(/,/g, ''));
          } else {
            // Fila 2 con celdas separadas: buscar la primera celda numérica válida
            for (let c = 0; c < row.length; c++) {
              const num = parseFloat(String(row[c] ?? '').replace(/[$,]/g, ''));
              if (!isNaN(num) && num > 0 && num < 1000 && !precioM3) {
                precioM3 = num;
                break;
              }
            }
          }
        }

        // 4. Capturar Periodo de Lectura
        // Ej: "* Lectura: 11 de agosto a 14 de septiembre", "Lectura: 13 de julio al 11 de agosto"
        if (/\*\s*lectura:?\s*([^\n\r]+)/i.test(rowStr) || (rowStr.toLowerCase().includes('lectura:') && !rowStr.toUpperCase().includes('ANTERIOR') && !rowStr.toUpperCase().includes('CASA') && !periodoLectura)) {
          const pMatch = rowStr.match(/lectura:?\s*([^*]+)/i);
          if (pMatch && pMatch[1].trim().length > 3) {
            periodoLectura = pMatch[1].trim();
          }
        }
      });

      // Extraer filas de las casas
      // Buscar la fila de encabezados que contenga 'CASA'
      let startHouseIdx = -1;
      rawRows.forEach((r, idx) => {
        if (startHouseIdx === -1 && r && r.some((cell) => String(cell || '').toUpperCase().includes('CASA'))) {
          startHouseIdx = idx + 1;
        }
      });

      if (startHouseIdx === -1) {
        // Fallback: buscar filas cuyo primer elemento sea un número entero (casa)
        startHouseIdx = 0;
      }

      for (let i = startHouseIdx; i < rawRows.length; i++) {
        const row = rawRows[i];
        if (!row || row.length === 0) continue;

        const casaNumVal = row[0];
        // Verificar que la primera columna contenga un identificador de casa válido
        if (
          casaNumVal !== null &&
          casaNumVal !== undefined &&
          String(casaNumVal).trim() !== '' &&
          !String(casaNumVal).toUpperCase().includes('CASA NO') &&
          !String(casaNumVal).toUpperCase().includes('PRECIO') &&
          !String(casaNumVal).toUpperCase().includes('TOTAL') &&
          !String(casaNumVal).toUpperCase().includes('CÁLCULO') &&
          !String(casaNumVal).toUpperCase().includes('INFORMACIÓN') &&
          !String(casaNumVal).startsWith('*')
        ) {
          const casaNumero = String(casaNumVal).trim();
          const lecturaAnterior = Math.round(Number(row[1] ?? 0) * 100) / 100;
          const lecturaActual = Math.round(Number(row[2] ?? 0) * 100) / 100;
          const consumoM3Raw = Number(row[3] ?? (lecturaActual >= lecturaAnterior ? lecturaActual - lecturaAnterior : 0));
          const consumoM3 = Math.round(consumoM3Raw * 100) / 100;
          const consumoPesosRaw = Number(row[4] ?? (consumoM3 * (precioM3 || 0)));
          const consumoPesos = Math.round(consumoPesosRaw);
          const prorrateoComun = Math.round(Number(row[5] ?? 0) * 100) / 100;
          const cargoTotalRaw = Number(row[6] ?? (consumoPesos + prorrateoComun));
          const cargoTotal = Math.round(cargoTotalRaw);

          casas.push({
            casaNumero: casaNumero,
            lecturaAnterior: isNaN(lecturaAnterior) ? 0 : lecturaAnterior,
            lecturaActual: isNaN(lecturaActual) ? 0 : lecturaActual,
            consumoM3: isNaN(consumoM3) ? 0 : consumoM3,
            consumoPesos: isNaN(consumoPesos) ? 0 : consumoPesos,
            prorrateoComun: isNaN(prorrateoComun) ? 0 : prorrateoComun,
            cargoTotal: isNaN(cargoTotal) ? 0 : cargoTotal,
            rawRowIndex: i + 1,
          });
        }
      }

      // Fallback 1: Si totalM3Fracc no se encontró en notas, sumamos el consumo de las casas
      if (!totalM3Fracc && casas.length > 0) {
        totalM3Fracc = Math.round(casas.reduce((acc, c) => acc + (c.consumoM3 || 0), 0) * 100) / 100;
      }

      // Fallback 2: Si precioM3 no se encontró explícitamente pero hay total CEA y M3 totales
      if (!precioM3 && totalReciboCEA > 0 && totalM3Fracc > 0) {
        precioM3 = totalReciboCEA / totalM3Fracc;
      }

      // Fallback 3: Si totalReciboCEA no vino pero sí hay precioM3 y totalM3Fracc
      if (!totalReciboCEA && precioM3 > 0 && totalM3Fracc > 0) {
        totalReciboCEA = Math.round(precioM3 * totalM3Fracc * 100) / 100;
      }

      precioM3 = Math.round((precioM3 || 0) * 100) / 100;

      // Si casas tenían consumoPesos en 0 pero ahora tenemos precioM3, calcular consumoPesos y cargoTotal
      if (precioM3 > 0) {
        casas.forEach((c) => {
          if (!c.consumoPesos && c.consumoM3 > 0) {
            c.consumoPesos = Math.round(c.consumoM3 * precioM3);
            c.cargoTotal = Math.round(c.consumoPesos + (c.prorrateoComun || 0));
          }
        });
      }

      // También revisar encabezado fila 0/1 para mes
      if (!periodoLectura) {
        rawRows.slice(0, 3).forEach((r) => {
          r.forEach((c) => {
            const val = String(c || '').trim();
            if (['ENERO', 'FEBRERO', 'MARZO', 'ABRIL', 'MAYO', 'JUNIO', 'JULIO', 'AGOSTO', 'SEPTIEMBRE', 'OCTUBRE', 'NOVIEMBRE', 'DICIEMBRE'].includes(val.toUpperCase())) {
              mesLectura = val.toUpperCase();
              periodoLectura = `Lectura ${val}`;
            }
          });
        });
      }

      if (casas.length === 0) {
        erroresFormato.push('No se encontraron filas con datos de casas válidos en el archivo.');
      }

      resolve({
        totalReciboCEA: Math.round((totalReciboCEA || 0) * 100) / 100,
        totalM3Fracc: Math.round((totalM3Fracc || 0) * 100) / 100,
        precioM3: Math.round((precioM3 || 0) * 100) / 100,
        periodoLectura: periodoLectura || 'Período no especificado',
        mesLectura: mesLectura || 'ACTUAL',
        anoLectura,
        casas,
        erroresFormato,
      });
    } catch (err) {
      reject(err);
    }
  });
};

/**
 * Genera y descarga una plantilla Excel de ejemplo con la estructura esperada para Consumo-Agua Potable.
 */
export const descargarPlantillaAgua = () => {
  const data = [
    ['OCTUBRE', '', '', '', 'CÁLCULO DE CONSUMO DE AGUA POTABLE'],
    ['PRECIO METRO CÚBICO:', 31.66, '', 'IMPORTE SERVICIO DE AGUA RECIBO:', 20621.86],
    [
      'CASA NO.',
      'LECTURA ANTERIOR (M3)',
      'LECTURA ACTUAL (M3)',
      'CONSUMO (M3)',
      'CONSUMO DE AGUA POR CASA ($)',
      'PRORRATEO ÁREAS COMUNES ($)',
      'CARGO TOTAL DE AGUA POR CASA ($)',
    ],
    [1, 221.0, 229.29, 8.29, 262.46, 0, 262.46],
    [2, 175.4, 182.39, 6.99, 221.30, 0, 221.30],
    [3, 113.36, 120.5, 7.14, 226.05, 0, 226.05],
    [4, 98.15, 105.4, 7.25, 229.54, 0, 229.54],
    [5, 150.0, 158.5, 8.5, 269.11, 0, 269.11],
    [],
    ['*', 'Importe servicio de agua recibo: $20,621.86'],
    ['*', 'Metros cúbicos condominio: 630.22'],
    ['*', 'Precio por M3 de acuerdo a consumo del condominio: $ 31.66'],
    ['*', 'Lectura: 11 de agosto a 14 de septiembre'],
  ];

  const ws = XLSX.utils.aoa_to_sheet(data);
  // Anchos de columna recomendados para máxima legibilidad
  ws['!cols'] = [
    { wch: 14 }, // CASA NO.
    { wch: 24 }, // LECTURA ANTERIOR
    { wch: 22 }, // LECTURA ACTUAL
    { wch: 16 }, // CONSUMO M3
    { wch: 30 }, // CONSUMO POR CASA
    { wch: 28 }, // PRORRATEO
    { wch: 32 }, // CARGO TOTAL
  ];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'AGUA CONSUMO');
  XLSX.writeFile(wb, 'Plantilla_Consumo_Agua_Potable.xlsx');
};

/**
 * Parsea el archivo PDF de consumo de agua mediante reconstrucción de coordenadas Y.
 */
export const parseAguaPdf = async (pdf) => {
  try {
    let totalReciboCEA = 0;
    let totalM3Fracc = 0;
    let precioM3 = 31.00; // Valor fallback
    let periodoLectura = 'Lectura período';
    let mesLectura = 'ACTUAL';
    let anoLectura = new Date().getFullYear();
    const casas = [];
    const erroresFormato = [];

    // Reconstruir todas las líneas del documento página por página
    const allLines = [];
    for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
      const page = await pdf.getPage(pageNum);
      const textContent = await page.getTextContent();

      // Agrupar por coordenada Y (tolerando pequeñas variaciones de hasta 3 puntos)
      const yGroups = {};
      textContent.items.forEach(item => {
        if (!item.str || item.str.trim() === '') return;

        let foundY = null;
        for (const key of Object.keys(yGroups)) {
          if (Math.abs(parseFloat(key) - item.transform[5]) < 3) {
            foundY = key;
            break;
          }
        }
        const groupKey = foundY || String(item.transform[5]);
        if (!yGroups[groupKey]) yGroups[groupKey] = [];
        yGroups[groupKey].push({ text: item.str, x: item.transform[4] });
      });

      // Ordenar las líneas de arriba a abajo
      const sortedYKeys = Object.keys(yGroups).sort((a, b) => parseFloat(b) - parseFloat(a));

      for (const yKey of sortedYKeys) {
        // Ordenar los fragmentos de izquierda a derecha
        const items = yGroups[yKey].sort((a, b) => a.x - b.x);
        const lineText = items.map(it => it.text).join(' ');
        allLines.push(lineText);

        // Detectar mes de lectura en las cabeceras
        const meses = ['ENERO', 'FEBRERO', 'MARZO', 'ABRIL', 'MAYO', 'JUNIO', 'JULIO', 'AGOSTO', 'SEPTIEMBRE', 'OCTUBRE', 'NOVIEMBRE', 'DICIEMBRE'];
        const lineUpper = lineText.toUpperCase();
        for (const m of meses) {
          if (lineUpper.includes(m)) {
            mesLectura = m;
            periodoLectura = `Lectura ${m}`;
          }
        }

        // Detectar precio por metro cúbico
        const pMatch = lineText.match(/(\d+(?:\.\d+)?)\s*\$\s*(?:A B|A\s*B)/i) || lineText.match(/PRECIO METRO CÚBICO:\s*\$\s*([\d.]+)/i);
        if (pMatch) {
          precioM3 = parseFloat(pMatch[1]);
        }
      }
    }

    // Procesar cada línea reconstruida
    allLines.forEach((line, idx) => {
      const trimmed = line.trim();
      // Separar por espacios múltiples y quitar los símbolos '$'
      const parts = trimmed.split(/\s+/).filter(p => p !== '' && p !== '$');

      if (parts.length >= 7) {
        const firstPart = parts[0];
        // Validar que el primer elemento de la fila sea un número de casa
        if (/^\d+$/.test(firstPart)) {
          const casaNumero = firstPart;

          const parseVal = (str) => {
            if (str === '-' || str === '-$' || str === '') return 0;
            return parseFloat(str.replace(/,/g, '')) || 0;
          };

          const lecturaAnterior = parseVal(parts[1]);
          const lecturaActual = parseVal(parts[2]);
          const consumoM3 = parseVal(parts[3]);
          const consumoPesos = parseVal(parts[4]);
          const prorrateoComun = parseVal(parts[5]);
          const cargoTotal = parseVal(parts[6]);

          casas.push({
            casaNumero,
            lecturaAnterior: Math.round(lecturaAnterior * 100) / 100,
            lecturaActual: Math.round(lecturaActual * 100) / 100,
            consumoM3: Math.round(consumoM3 * 100) / 100,
            consumoPesos: Math.round(consumoPesos),
            prorrateoComun: Math.round(prorrateoComun * 100) / 100,
            cargoTotal: Math.round(cargoTotal),
            rawRowIndex: idx + 1
          });

          totalM3Fracc += consumoM3;
          totalReciboCEA += cargoTotal;
        }
      }
    });

    if (casas.length === 0) {
      erroresFormato.push('No se encontraron filas con datos de casas válidos en el archivo PDF.');
    }

    return {
      totalReciboCEA: Math.round(totalReciboCEA * 100) / 100,
      totalM3Fracc: Math.round(totalM3Fracc * 100) / 100,
      precioM3: Math.round(precioM3 * 100) / 100,
      periodoLectura,
      mesLectura,
      anoLectura,
      casas,
      erroresFormato
    };
  } catch (err) {
    throw err;
  }
};

